import { CircleCheck, Info, TriangleAlert, OctagonX, X, PartyPopper, LucideIcon } from 'lucide-react';
import { toast } from 'sonner';
import { triggerCelebration } from '@/hooks/useConfetti';
import { playCelebrationSound } from '@/components/ui/celebration-toast';

interface CustomToastProps {
  t: string | number;
  title: string;
  description?: string;
  icon: LucideIcon;
  variant?: 'default' | 'celebration';
  onClick?: () => void;
}

function CustomToast({ t, title, description, icon: Icon, variant = 'default', onClick }: CustomToastProps) {
  const handleClick = () => {
    if (onClick) {
      onClick();
      toast.dismiss(t);
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    toast.dismiss(t);
  };

  const isCelebration = variant === 'celebration';

  return (
    <div
      onClick={onClick ? handleClick : undefined}
      className="relative flex items-center gap-3 rounded-lg transition-all duration-200 hover:translate-x-[-4px]"
      style={{
        width: '380px',
        padding: '16px',
        backgroundColor: 'var(--pm-toast-bg)',
        border: isCelebration ? '1px solid var(--pm-cyan)' : '1px solid var(--pm-toast-border)',
        boxShadow: isCelebration
          ? '0 0 20px var(--pm-cyan-glow), 0 10px 15px -3px rgba(0, 0, 0, 0.1)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
        cursor: onClick ? 'pointer' : 'default',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--pm-toast-bg-hover)';
        if (!isCelebration) {
          e.currentTarget.style.borderColor = 'var(--pm-toast-border-hover)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--pm-toast-bg)';
        if (!isCelebration) {
          e.currentTarget.style.borderColor = 'var(--pm-toast-border)';
        }
      }}
    >
      {/* Icon with circular background */}
      <div
        className="flex-shrink-0 flex items-center justify-center rounded-full"
        style={{
          width: '32px',
          height: '32px',
          backgroundColor: 'var(--pm-toast-icon-bg)',
        }}
      >
        <Icon style={{ width: '16px', height: '16px', color: 'var(--pm-cyan-text)' }} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className="font-medium"
          style={{ color: 'var(--pm-text-primary)', fontSize: '14px' }}
        >
          {title}
        </p>
        {description && (
          <p
            style={{ color: 'var(--pm-text-muted)', fontSize: '13px', marginTop: '2px' }}
          >
            {description}
          </p>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={handleClose}
        className="flex-shrink-0 transition-opacity"
        style={{ color: 'var(--pm-text-muted)', opacity: 0.5 }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; }}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// Toast options interface
interface ToastOptions {
  description?: string;
  duration?: number;
  onClick?: () => void;
}

// Create toast helper
function createToast(
  title: string,
  icon: LucideIcon,
  options: ToastOptions & { variant?: 'default' | 'celebration' } = {}
) {
  const { description, duration = 5000, onClick, variant = 'default' } = options;

  return toast.custom(
    (t) => (
      <CustomToast
        t={t}
        title={title}
        description={description}
        icon={icon}
        variant={variant}
        onClick={onClick}
      />
    ),
    {
      duration,
      position: 'top-right',
    }
  );
}

// Export individual toast functions
export const customToast = {
  success: (title: string, options?: ToastOptions) =>
    createToast(title, CircleCheck, options),

  error: (title: string, options?: ToastOptions) =>
    createToast(title, OctagonX, options),

  info: (title: string, options?: ToastOptions) =>
    createToast(title, Info, options),

  warning: (title: string, options?: ToastOptions) =>
    createToast(title, TriangleAlert, options),

  celebration: (title: string, options?: ToastOptions & { withConfetti?: boolean; withSound?: boolean }) => {
    const { withConfetti = true, withSound = true, ...restOptions } = options || {};

    if (withConfetti) {
      triggerCelebration();
    }
    if (withSound) {
      playCelebrationSound();
    }

    return createToast(title, PartyPopper, {
      ...restOptions,
      variant: 'celebration',
      duration: restOptions.duration || 8000,
    });
  },
};

export { CustomToast };
